// File: frontend/src/services/emailService.ts

import { apiClient } from "./apiClient";
import { API_BASE_URL } from "../constants";

/**
 * Self-service email change.
 * Requires the user's current password for verification.
 */
export const changeEmail = async (
  currentPassword: string,
  newEmail: string
): Promise<void> => {
  await apiClient.put("/api/profile/me/email", { currentPassword, newEmail });
};

/**
 * Ask the backend for a fresh email-verification link.
 *
 * Called from two places, and each supplies whichever handle it has:
 *   - the expired-link page passes the old `token` straight out of the URL
 *   - the login form passes the `identifier` the user already typed
 *
 * Uses fetch() rather than apiClient because the backend picks the language of
 * the email from Accept-Language, and apiClient has no way to add headers. The
 * caller reads the active language from i18next and passes it in, which keeps
 * this module free of an i18n import.
 *
 * Resolves for every outcome except the resend cooldown — the backend answers
 * 200 even for an unknown or already-verified account on purpose, so that the
 * endpoint cannot be used to find out which addresses are registered. A cooldown
 * rejection is thrown as an Error carrying the backend code
 * ("VERIFICATION_RESEND_COOLDOWN:{seconds}") for parseApiError to translate.
 */
export const resendVerificationEmail = async (
  params: { identifier?: string; token?: string },
  language: string
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/api/auth/resend-verification`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": language,
    },
    body: JSON.stringify({
      identifier: params.identifier ?? null,
      token: params.token ?? null,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message =
      errorData?.message ||
      errorData?.error ||
      `Resend failed with status: ${response.status}`;
    throw new Error(message);
  }
};